import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';

import { API } from '../common';
import { authService } from './auth-service';

// Create axios instance
export const axiosInstance = axios.create({
    baseURL: API.RAPI_URL,
    timeout: 30000,
    withCredentials: true, // Enable sending cookies for refresh token
});

// Flag to prevent multiple refresh requests
let isRefreshing = false;
let refreshPromise: Promise<string> | null = null;

// Request interceptor - check token expiration and refresh if needed
axiosInstance.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
        // Check if token is expired
        const isExpired = authService.isTokenExpired();

        if (isExpired) {
            // If refresh is already in progress, wait for it
            if (isRefreshing && refreshPromise) {
                try {
                    const newToken = await refreshPromise;
                    config.headers.Authorization = `Bearer ${newToken}`;
                    config.withCredentials = false;
                    return config;
                } catch (error) {
                    return Promise.reject(error);
                }
            }

            // Start refresh process
            isRefreshing = true;
            refreshPromise = authService
                .refreshToken()
                .then((newToken) => {
                    isRefreshing = false;
                    refreshPromise = null;
                    return newToken;
                })
                .catch((error) => {
                    isRefreshing = false;
                    refreshPromise = null;
                    authService.logout();
                    window.location.reload();
                    throw error;
                });

            try {
                const newToken = await refreshPromise;
                config.headers.Authorization = `Bearer ${newToken}`;
                config.withCredentials = false;
            } catch (error) {
                return Promise.reject(error);
            }
        } else {
            const token = authService.getToken();
            config.withCredentials = false;
            if (token) {
                config.headers.Authorization = `Bearer ${token}`;
            }
        }

        return config;
    },
    (error) => {
        return Promise.reject(error);
    },
);

// Response interceptor - handle other errors
axiosInstance.interceptors.response.use(
    (response) => {
        return response;
    },
    async (error: AxiosError) => {
        console.log('_+_+_+_+_+_+', error);

        // If 401 still happens after token refresh, logout
        if (error.response?.status === 401) {
            authService.logout();
            window.location.reload();
        }

        return Promise.reject(error);
    },
);

export default axiosInstance;
